import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

/**
 * Custom hook that automatically redirects users to the home page when they become unauthenticated.
 * This handles ALL logout scenarios:
 * - Manual logout clicks
 * - Token expiration
 * - 401 API responses 
 * - Server-side session invalidation
 * - Multiple tab scenarios
 * 
 * The hook monitors the authentication state and triggers navigation when isAuthenticated 
 * changes from true to false, ensuring users never remain on protected pages after logout.
 */
export const useAuthRedirect = () => {
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const previousAuthState = useRef<boolean>(isAuthenticated);

  useEffect(() => {
    // Track authentication state changes
    const wasAuthenticated = previousAuthState.current;
    const isCurrentlyAuthenticated = isAuthenticated;
    
    // Update the ref for next comparison
    previousAuthState.current = isCurrentlyAuthenticated;

    // If user went from authenticated to unauthenticated, redirect to home
    if (wasAuthenticated && !isCurrentlyAuthenticated) {
      console.log('🔄 Auth state changed: User logged out, redirecting to home page');
      
      // Don't redirect if already on public pages
      const publicPaths = ['/', '/login', '/register', '/forgot-password', '/reset-password', '/about', '/contact', '/news'];
      const isOnPublicPage = publicPaths.some(path => 
        location.pathname === path || location.pathname.startsWith('/design')
      );
      
      if (!isOnPublicPage) {
        // Use replace to prevent going back to protected pages
        navigate('/', { replace: true });
      }
    }
  }, [isAuthenticated, navigate, location.pathname]);

  return {
    isAuthenticated,
    currentPath: location.pathname
  };
};